import moment, { type Moment } from "moment";
import { store } from "../../_Bootstrap/RoutingAndStore";
import { type QueuedItemData, type QueuedReturn } from "../../Data/_Bases/BaseUploadRepository";
import {
    getOption,
    isTrainingMode,
    subscribeToOption,
} from "../../Utilities/Platform/Options/Options";
import AgentManager from "../Bases/AgentManager";
import BaseAgent, { generateWorkerURL } from "../Bases/BaseAgent";
import { UPLOAD_AGENT_EVENTS, UPLOAD_TYPES, UPLOAD_WORKER_EVENTS } from "./UploadEvents";

type ErrorCallback = (id?: number, type?: string, data?: string) => void;

export default class UploadAgent extends BaseAgent {
    protected offline: () => void;
    protected online: () => void;
    protected error: ErrorCallback;
    protected unauthenticated: () => void;

    constructor(offline: () => void, online: () => void, unauthenticated: () => void, error: ErrorCallback) {
        super(generateWorkerURL("uploadworker"), "Shopfront Uploader");

        this.offline = offline;
        this.online = online;
        this.error = error;
        this.unauthenticated = unauthenticated;

        // Start the worker
        this.startWorker();

        // Subscribe to option changes
        this.handleInstanceChange(
            "_instance_id",
            subscribeToOption("_instance_id", this.handleInstanceChange),
            ""
        );
    }

    protected handleInstanceChange = (_: "_instance_id", newValue: string | false, oldValue: string | false): void => {
        if(newValue !== oldValue) {
            this.sendMessage(UPLOAD_AGENT_EVENTS.INSTANCE_ID, newValue);
        }
    };

    public handleMessage(data: {
        type: UPLOAD_WORKER_EVENTS;
        message: any;
    }): void {
        if(!data.type) {
            console.error("Unknown data passed to UploadAgent", data);

            return;
        }

        switch(data.type) {
            case UPLOAD_WORKER_EVENTS.UPLOAD_OFFLINE:
                this.offline();
                break;
            case UPLOAD_WORKER_EVENTS.UPLOAD_ONLINE:
                this.online();
                break;
            case UPLOAD_WORKER_EVENTS.UPLOAD_UNAUTHENTICATED:
                this.unauthenticated();
                break;
            case UPLOAD_WORKER_EVENTS.UPLOAD_FAILED:
                this.error(data.message.id, data.message.type, data.message.data);
                break;
            case UPLOAD_WORKER_EVENTS.REQUEST_LOCATION:
                this.sendMessage(UPLOAD_AGENT_EVENTS.LOCATION, {
                    register: getOption("register"),
                    outlet  : getOption("outlet"),
                    request : data.message,
                });
                break;
        }
    }
}

/**
 * Queue something to be sent to the server
 */
export function upload<Type extends UPLOAD_TYPES>(
    type: Type,
    data: QueuedItemData[Type]
): Promise<QueuedReturn[Type]> {
    // Training data must never reach the server. Actions and security events are exempt because they
    // do their own check, which deliberately lets the "training mode disabled" pair through
    if(type !== UPLOAD_TYPES.ACTION && type !== UPLOAD_TYPES.SECURITY_EVENT && isTrainingMode()) {
        return Promise.resolve(undefined as QueuedReturn[Type]);
    }

    return new Promise(res => {
        AgentManager.getAgent(UploadAgent).sendMessage(UPLOAD_AGENT_EVENTS.UPLOAD, {
            type,
            data: {
                id: null,
                data,
            },
        }, res);
    });
}

export function uploadStocktakeCount(stocktake: string, id: number): Promise<void> {
    return upload(UPLOAD_TYPES.STOCKTAKE_COUNT, {
        stocktake,
        items: [ id ],
    });
}

export function runUploadQueue(force = false, reset = false, resendEmail = false): Promise<void> {
    return new Promise(res => {
        AgentManager.getAgent(UploadAgent).sendMessage(UPLOAD_AGENT_EVENTS.RUN_QUEUE, {
            force,
            reset,
            resendEmail,
        }, res);
    });
}

export function sendAction(
    action: string,
    affectingType: string,
    affectingId: string,
    additional: string | Record<string, unknown> | null = null,
    createdAt?: string
): Promise<void | boolean> {
    if(action !== "TRAINING_MODE" && isTrainingMode()) {
        return Promise.resolve(false);
    }

    const state = store.getState();
    const user = state.user.id;

    if(!user) {
        return Promise.resolve(false);
    }

    if(typeof createdAt === "undefined") {
        createdAt = moment().toISOString();
    }

    if(additional && typeof additional !== "string") {
        additional = JSON.stringify(additional);
    }

    return upload(UPLOAD_TYPES.ACTION, {
        user,
        affectingType,
        affectingId,
        action,
        additional,
        createdAt,
    });
}

export function sendSecurityEvent(
    event:
        "LOCATION_CHANGE" |
        "OPEN_DRAWER" |
        "CLEAR_DATA" |
        "TRAINING_MODE" |
        "REMOVED_PRODUCT" |
        "PARKED_SALE" |
        "CANCELLED_SALE" |
        "UNPARKED_SALE",
    metaData: Record<string, unknown>,
    timestamp?: string | Moment
): Promise<void | false> {
    if(event !== "TRAINING_MODE" && isTrainingMode()) {
        return Promise.resolve(false);
    }

    const state = store.getState();
    let user: string | null | false = state.user.id;
    const outlet: string | null = state.location.outlet?.id || null;
    const register: string | null = state.location.register?.id || null;

    if(!user) {
        user = null;
    }

    return upload(UPLOAD_TYPES.SECURITY_EVENT, {
        user,
        outlet,
        register,
        event,
        metaData : JSON.stringify(metaData),
        timestamp: moment(timestamp).toISOString(),
    });
}
